import { useCallback, useState } from 'react';

/**
 * Represents the state of an asynchronous operation.
 * `snapshotId` is the unique id of the last snapshot, used to determine if the state is stale.
 */
export type LoadState<T> =
  | { status: 'loading'; snapshotId: string }
  | { status: 'loaded'; snapshotId: string; data: T }
  | { status: 'error'; snapshotId: string; error: Error };

export const useLoadState = <T>(
  props:
    | { status: 'loading' }
    | { status: 'loaded'; data: T }
    | { status: 'error'; error: Error },
) => {
  const [loadState, _setLoadState] = useState<LoadState<T>>({
    ...props,
    snapshotId: makeSnapshotId(props.status),
  });
  const setLoadState = useCallback(
    (
      props:
        | { status: 'loading' }
        | { status: 'loaded'; data: T }
        | { status: 'error'; error: Error }
        | ((current: LoadState<T>) => LoadState<T>),
    ) => {
      _setLoadState((current) => {
        if (typeof props === 'function') {
          return props(current);
        }
        return { ...props, snapshotId: makeSnapshotId(props.status) };
      });
    },
    [],
  );
  return { loadState, setLoadState };
};

export const makeLoadState = <T>(
  props:
    | { status: 'loading' }
    | { status: 'loaded'; data: T }
    | { status: 'error'; error: Error },
) => {
  return {
    ...props,
    snapshotId: makeSnapshotId(props.status),
  };
};

const makeSnapshotId = (status: string) => `${status}-${Date.now()}`;
